export const getUploadId = (value) => {
  if (!value) return 0;

  if (typeof value === "number") return value;

  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  if (typeof value === "object") {
    return (
      value.id ||
      value.data?.id ||
      value.data ||
      value.file_id ||
      value.upload_id ||
      value.id_upload ||
      value.dataValues?.id ||
      0
    );
  }

  return 0;
};

export const isEmptyValue = (value) => {
  return (
    value === null ||
    value === undefined ||
    value === 0 ||
    (typeof value === "string" && value.trim() === "") ||
    (Array.isArray(value) && value.length === 0)
  );
};

export const normalizeArray = (value) => {
  if (Array.isArray(value)) return value;

  if (typeof value === "string") {
    if (value.trim() === "") return [];

    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  return [];
};

export const isEmptyArray = (value) => {
  return normalizeArray(value).length === 0;
};

export const normalizeEmails = (value) => {
  if (Array.isArray(value)) return value;

  if (typeof value === "string") {
    if (value.trim() === "") return [];

    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  return [];
};

export const hasAtLeastOneEmail = (value) => {
  const emails = normalizeEmails(value);

  return emails.some((email) => {
    return typeof email === "string" && email.trim() !== "";
  });
};

export const isYesNoSelected = (value) => {
  return value === 0 || value === 1 || value === "0" || value === "1";
};

export const validateProspectDocuments = (data) => {
  const errors = [];

  if (getUploadId(data.document_arca) === 0) {
    errors.push("document_arca");
  }

  if (getUploadId(data.btn_ccbu) === 0) {
    errors.push("btn_ccbu");
  }

  if (
    Number(data.iigg_exento) === 1 &&
    getUploadId(data.iigg_exento_file) === 0
  ) {
    errors.push("iigg_exento_file");
  }

  if (Number(data.sf_exento) === 1 && getUploadId(data.sf_exento_file) === 0) {
    errors.push("sf_exento_file");
  }

  if (
    Number(data.bsas_exento) === 1 &&
    getUploadId(data.bsas_exento_file) === 0
  ) {
    errors.push("bsas_exento_file");
  }

  if (
    Number(data.caba_exento) === 1 &&
    getUploadId(data.caba_exento_file) === 0
  ) {
    errors.push("caba_exento_file");
  }

  if (
    [1, 3, 5].includes(Number(data.iibb_category)) &&
    getUploadId(data.iibb_category_file) === 0
  ) {
    errors.push("iibb_category_file");
  }
  return errors;
};

export const validateProspectAdministrativeInputs = (data, { isSubUser = false } = {}) => {
  const errors = [];

  // Solo un subusuario puede no facturar; invoice_flag es 0 por defecto para todos los prospects
  if (isSubUser && Number(data.invoice_flag) !== 1) {
    return errors;
  }

  if (isEmptyArray(data.billing_societies)) {
    errors.push("billing_societies");
  }

  if (!isYesNoSelected(data.iigg_exento)) {
    errors.push("iigg_exento");
  }

  if (!isYesNoSelected(data.sf_exento)) {
    errors.push("sf_exento");
  }

  if (!isYesNoSelected(data.bsas_exento)) {
    errors.push("bsas_exento");
  }

  if (!isYesNoSelected(data.caba_exento)) {
    errors.push("caba_exento");
  }

  if (isEmptyArray(data.iibb_jurisdicciones)) {
    errors.push("iibb_jurisdicciones");
  }

  if (!hasAtLeastOneEmail(data.email_retenciones)) {
    errors.push("email_retenciones");
  }

  if (!isYesNoSelected(data.iigg_agente_retencion)) {
  errors.push("iigg_agente_retencion");
  }
  
  // El id 5 fue eliminado "Régimen simplificado" en listIibbCondicion. 
  // Si un candidato quedó con ese valor legacy, lo tratamos
  // como incompleto para forzar que se vuelva a seleccionar una opción vigente.
  if (isEmptyValue(data.iibb_condicion) || Number(data.iibb_condicion) === 5) {
    errors.push("iibb_condicion");
  }

  if (isEmptyValue(data.iibb_category)) {
    errors.push("iibb_category");
  }

  if (!isYesNoSelected(data.is_iva_exento)) {
  errors.push("is_iva_exento");
  }

  return errors;
};

export const PROSPECT_VALIDATION_LABELS = {
  document_arca: "Constancia de ARCA",
  btn_ccbu: "Constancia de CBU",

  billing_societies: "Sociedades de facturación",
  iigg_exento: "IIGG Exento",
  sf_exento: "Exención Santa Fe",
  bsas_exento: "Exención Buenos Aires",
  caba_exento: "Exención CABA",
  is_iva_exento: "Exento IVA",
  iigg_agente_retencion: "IIGG Agente Retención",
  iibb_condicion: "IIBB Condición",
  iibb_category: "IIBB Categoría",
  iibb_jurisdicciones: "IIBB Jurisdicciones",
  email_retenciones: "Emails para retenciones",

  iigg_exento_file: "Certificado IIGG Exento",
  sf_exento_file: "Certificado Exención Santa Fe",
  bsas_exento_file: "Certificado Exención Buenos Aires",
  caba_exento_file: "Certificado Exención CABA",
  iibb_category_file: "Certificado IIBB Categoría (Convenio Multilateral / Convenio Multilateral Especial / Régimen Simplificado)",
};

export const buildProspectValidationList = (remainingFields) => {
  return remainingFields.map((fieldKey) => {
    const label = document.querySelector(`[for="${fieldKey}"]`);

    return {
      id: fieldKey,
      text:
        PROSPECT_VALIDATION_LABELS[fieldKey] ||
        (label ? label.innerText : fieldKey),
    };
  });
};
